import React, { useRef, useEffect, useState, Fragment } from "react";
import { connect } from "react-redux";
import { storeCanvas } from "../../actions/imageActions";

const Canvas = ({ canvas, storeCanvas, displayAlert }) => {
  const canvasRef = useRef(null);
  const cRef = useRef(null);
  const maxHistorySize = 10;

  // available tools:  pencil, eraser
  const [tool, setTool] = useState("pencil");
  const [isDrawing, setIsDrawing] = useState(false);
  const [lineWidth, setLineWidth] = useState(5);
  const [color, setColor] = useState("ffffff");
  const [isUndoOn, setIsUndoOn] = useState(false);
  const [stroke, setStroke] = useState([]);
  const [undoCount, setUndoCount] = useState(0);

  // draw history stores snaps of canvas at the end of every stroke
  // in base64 format
  const [drawHistory, setDrawHistory] = useState({
    history: [],
    index: -1,
  });

  useEffect(() => {
    setUpCanvas();
    if (canvas) {
      const img = new Image();
      img.onload = function () {
        cRef.current.drawImage(img, 0, 0);
      };
      img.src = canvas.history[canvas.index];
    }

    // listen to screen resize
    window.addEventListener("resize", handleResize);

    // load and draw image from store
    if (!canvas || canvas?.history?.length === 0) {
      return;
    }

    // setup history from cache
    setDrawHistory(canvas);

    // cleanup on unmount
    return () => {
      window.removeEventListener("resize", setUpCanvas);
    };
  }, []);

  useEffect(() => {
    // store history to canvas when history changes
    storeCanvas(drawHistory);
    // undo
    if (!isUndoOn) {
      return;
    }

    // imageData
    // cRef.current.putImageData(drawHistory.history[drawHistory.index], 0, 0);

    // base64
    const img = new Image();
    img.onload = function () {
      cRef.current.drawImage(img, 0, 0);
    };
    img.src = drawHistory.history[drawHistory.index];

    setIsUndoOn(false);
  }, [drawHistory]);

  useEffect(() => {
    // cant prevent default if we dont use passive listeners for some reasons
    const canvas = document.querySelector("#canvas");

    const handleTouchmove = (e) => {
      e.preventDefault();
      if (!isDrawing) return;

      const { clientX, clientY } = e.changedTouches[0];
      const { offsetLeft, offsetTop } = canvas;
      const X = clientX - offsetLeft;
      const Y = clientY - offsetTop;
      setStroke((stroke) => [...stroke, { X, Y }]);
    };

    const handleMousemove = (e) => {
      e.preventDefault();
      if (!isDrawing) return;

      const { clientX, clientY } = e;
      const { offsetLeft, offsetTop } = canvas;
      const X = clientX - offsetLeft;
      const Y = clientY - offsetTop;
      setStroke((stroke) => [...stroke, { X, Y }]);
    };

    canvas.addEventListener("touchmove", handleTouchmove);
    canvas.addEventListener("mousemove", handleMousemove);
    return () => {
      canvas.removeEventListener("touchmove", handleTouchmove);
      canvas.removeEventListener("mousemove", handleMousemove);
    };
  }, [isDrawing]);

  useEffect(() => {
    drawOnCanvas(stroke);
  }, [stroke]);

  useEffect(() => {
    changeColor();
  }, [color]);

  function drawOnCanvas(stroke) {
    const l = stroke.length - 1;
    if (stroke.length >= 3) {
      const xc = (stroke[l].X + stroke[l - 1].X) / 2;
      const yc = (stroke[l].Y + stroke[l - 1].Y) / 2;
      cRef.current.quadraticCurveTo(stroke[l - 1].X, stroke[l - 1].Y, xc, yc);
      cRef.current.stroke();
      cRef.current.beginPath();
      cRef.current.moveTo(xc, yc);
    } else {
      const point = stroke[l];
      if (!point) return;
      cRef.current.beginPath();
      cRef.current.moveTo(point.X, point.Y);
      cRef.current.stroke();
    }
  }

  const handleResize = () => {
    if (!canvasRef?.current) {
      return;
    }
    canvasRef.current.width = window.innerWidth - 90;
    canvasRef.current.height = window.innerHeight - 130;

    //
    // const image = new Image();
    // image.onload = () => {
    //   cRef.current.drawImage(image, 0, 0);
    // };
    // image.src = canvas.history[canvas.index];
  };

  const setUpCanvas = () => {
    const canvas = canvasRef.current;

    // left gutter => 74px
    // top gutter => 71px
    // bottom gutter => 55px
    if (!canvas) {
      return;
    }
    canvas.width = window.innerWidth - 90;
    canvas.height = window.innerHeight - 130;

    const c = canvas.getContext("2d");
    // to support increased screen density
    // c.scale(2, 2);

    // white background
    c.fillStyle = "#fff";
    c.fillRect(0, 0, canvas.width, canvas.height);

    // canvas initial settings
    c.fillStyle = "#000";
    c.lineCap = "round";
    c.strokeStyle = "black";
    c.lineJoin = "round";
    c.lineWidth = lineWidth;

    // save context in ref so that it persisits
    cRef.current = c;
  };

  const startDrawing = async ({ nativeEvent }) => {
    const { clientX, clientY } = nativeEvent;
    const { offsetLeft, offsetTop } = canvasRef.current;
    const X = clientX - offsetLeft;
    const Y = clientY - offsetTop;

    cRef.current.strokeStyle = "#000";
    cRef.current.lineWidth = lineWidth;
    cRef.current.beginPath();

    setIsDrawing(true);
    if (tool === "pencil") {
      cRef.current.strokeStyle = color;
      cRef.current.lineWidth = lineWidth;
    }
    if (tool === "eraser") {
      cRef.current.strokeStyle = "#fff";
      cRef.current.lineWidth = lineWidth * 15;
    }

    setStroke([{ X, Y }]);
    cRef.current.beginPath();
    cRef.current.moveTo(X, Y);
  };

  const finishDrawing = ({ nativeEvent }) => {
    if (!isDrawing) return;
    if (tool === "pencil") {
      const { clientX, clientY } = nativeEvent;
      const { offsetLeft, offsetTop } = canvasRef.current;
      const X = clientX - offsetLeft;
      const Y = clientY - offsetTop;
      cRef.current.lineTo(X, Y);
      cRef.current.stroke();
      cRef.current.closePath();
    }
    setStroke([]);

    setIsDrawing(false);

    // add current canvas data to history
    if (nativeEvent.type === "touchleave" || nativeEvent.type === "mouseout") {
      return;
    }
    // base64
    // if user draws anything after an undo, clear all history above the current index
    // if index is already at end of history, just add the current snap to its end
    if (drawHistory.index < drawHistory.history.length - 1) {
      const newHistory = drawHistory.history.slice(0, drawHistory.index + 1);
      setDrawHistory((drawHistory) => ({
        history: [...newHistory, canvasRef.current.toDataURL()],
        index: drawHistory.index + 1,
      }));
    } else {
      if (drawHistory.history.length >= maxHistorySize) {
        setDrawHistory((drawHistory) => ({
          history: [
            ...drawHistory.history.slice(1),
            canvasRef.current.toDataURL(),
          ],
          index: Math.min(drawHistory.index + 1, maxHistorySize - 1),
        }));
      } else {
        setDrawHistory((drawHistory) => ({
          history: [...drawHistory.history, canvasRef.current.toDataURL()],
          index: Math.min(drawHistory.index + 1, maxHistorySize - 1),
        }));
      }
    }

    setUndoCount(0);
  };

  const clear = () => {
    cRef.current.fillStyle = "#fff";
    cRef.current.fillRect(
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height
    );
    cRef.current.fillStyle = "#000";
    setTool("pencil");

    // set history to initial state
    setDrawHistory({
      history: [],
      index: -1,
    });

    setUndoCount(0);
  };

  const redo = () => {
    // return if already at last canvas snap
    if (drawHistory.index >= drawHistory.history.length - 1) return;

    // otherwise move to one index above

    setIsUndoOn(true);
    setDrawHistory((drawHistory) => ({
      history: drawHistory.history,
      index: drawHistory.index + 1,
    }));
  };

  const undo = () => {
    console.log(undoCount);
    // if index -1 , just clear everything
    if (drawHistory.index <= 0) {
      if (undoCount >= maxHistorySize - 1) {
        return displayAlert("Max Undo limit reached!");
      } else {
        return clear();
      }
    }
    // otherwise get one index down in history

    setIsUndoOn(true);
    console.log("index: " + drawHistory.index);
    console.log("length: " + drawHistory.history.length);
    setDrawHistory((drawHistory) => ({
      ...drawHistory,
      index: drawHistory.index - 1,
    }));
    setUndoCount((undoCount) => ++undoCount);
  };

  const onSubmit = () => {
    // crop the image to remove blank space, then upload

    var myImage = new Image();
    myImage.crossOrigin = "Anonymous";
    myImage.onload = function () {
      var imageData = removeImageBlanks(myImage); //Will return cropped image data
    };
    myImage.src = canvas.history[canvas.index];

    //-----------------------------------------//
    function removeImageBlanks(imageObject) {
      var imgWidth = imageObject.width;
      var imgHeight = imageObject.height;
      var canvas = document.createElement("canvas");
      canvas.setAttribute("width", imgWidth);
      canvas.setAttribute("height", imgHeight);
      var context = canvas.getContext("2d");
      context.drawImage(imageObject, 0, 0);

      var imageData = context.getImageData(0, 0, imgWidth, imgHeight),
        data = imageData.data,
        getRBG = function (x, y) {
          var offset = imgWidth * y + x;
          return {
            red: data[offset * 4],
            green: data[offset * 4 + 1],
            blue: data[offset * 4 + 2],
            opacity: data[offset * 4 + 3],
          };
        },
        isWhite = function (rgb) {
          // many images contain noise, as the white is not a pure #fff white
          return rgb.red > 200 && rgb.green > 200 && rgb.blue > 200;
        },
        scanY = function (fromTop) {
          var offset = fromTop ? 1 : -1;

          // loop through each row
          for (
            var y = fromTop ? 0 : imgHeight - 1;
            fromTop ? y < imgHeight : y > -1;
            y += offset
          ) {
            // loop through each column
            for (var x = 0; x < imgWidth; x++) {
              var rgb = getRBG(x, y);
              if (!isWhite(rgb)) {
                if (fromTop) {
                  return y;
                } else {
                  return Math.min(y + 1, imgHeight);
                }
              }
            }
          }
          return null; // all image is white
        },
        scanX = function (fromLeft) {
          var offset = fromLeft ? 1 : -1;

          // loop through each column
          for (
            var x = fromLeft ? 0 : imgWidth - 1;
            fromLeft ? x < imgWidth : x > -1;
            x += offset
          ) {
            // loop through each row
            for (var y = 0; y < imgHeight; y++) {
              var rgb = getRBG(x, y);
              if (!isWhite(rgb)) {
                if (fromLeft) {
                  return x;
                } else {
                  return Math.min(x + 1, imgWidth);
                }
              }
            }
          }
          return null; // all image is white
        };

      // crop image to auto fit, and set a gutter of 10%
      var cropTop = scanY(true),
        cropBottom = scanY(false),
        cropLeft = scanX(true),
        cropRight = scanX(false),
        cropWidth = cropRight - cropLeft,
        cropHeight = cropBottom - cropTop;

      canvas.setAttribute("width", cropWidth + 0.2 * cropWidth);
      canvas.setAttribute("height", cropHeight + 0.2 * cropHeight);
      canvas.getContext("2d").fillStyle = "#fff";
      canvas.getContext("2d").fillRect(0, 0, canvas.width, canvas.height);
      // finally crop the guy
      canvas.getContext("2d").translate(0.1 * cropWidth, 0.1 * cropHeight);

      canvas
        .getContext("2d")
        .drawImage(
          imageObject,
          cropLeft,
          cropTop,
          cropWidth,
          cropHeight,
          0,
          0,
          cropWidth,
          cropHeight
        );

      // return canvas.toDataURL();

      const image = canvas.toDataURL("image/png");

      let a = document.createElement("a");
      document.body.appendChild(a);
      a.download = "my sketch";
      a.href = image;
      a.click();
    }
  };

  function changeColor() {
    canvasRef.current.strokeStyle = color;
  }

  return (
    <Fragment>
      <button
        id="submit"
        className="btn primary-btn canvas-submit"
        onClick={onSubmit}
      >
        Download
        <i className="fas fa-download" style={{ marginLeft: "5px" }}></i>
      </button>

      <div className="tools">
        <button
          name="pencil"
          className={`btn pencil tool ${
            tool === "pencil" ? "active-tool" : ""
          }`}
          onClick={() => setTool("pencil")}
        >
          <i className="fas fa-pencil-alt fa-2x"></i>
        </button>
        <button
          name="eraser"
          className={`btn eraser tool ${
            tool === "eraser" ? "active-tool" : ""
          }`}
          onClick={() => setTool("eraser")}
        >
          <i className="fas fa-eraser fa-2x"></i>
        </button>
        <button name="undo" className="btn undo tool" onClick={undo}>
          <i className="fas fa-undo fa-2x"></i>
        </button>
        <button name="redo" className="btn redo tool" onClick={redo}>
          <i className="fas fa-redo fa-2x"></i>
        </button>
        <input
          type="color"
          id="favcolor"
          name="favcolor"
          value={color}
          style={{
            width: "33px",
            height: "33px",
            borderRadius: "16px",
            border: "none",
            outline: "none",
            boxShadow: "none",
            margin: "6px 12px",
            overflow: "hidden",
          }}
          onChange={(e) => setColor(e.target.value)}
        />
      </div>

      <canvas
        id="canvas"
        className="canvas"
        onMouseDown={startDrawing}
        onTouchStart={startDrawing}
        // onMouseMove={draw}
        onMouseUp={finishDrawing}
        onMouseLeave={finishDrawing}
        onTouchEnd={finishDrawing}
        ref={canvasRef}
      ></canvas>

      <div className="options">
        <button className="btn  canvas-clear" onClick={clear}>
          Clear
          <i className="fas fa-trash-alt " style={{ marginLeft: "5px" }}></i>
        </button>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  canvas: state.imageData.canvas,
});

export default connect(mapStateToProps, { storeCanvas })(Canvas);
